import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaCheckCircle, FaGraduationCap, FaUsers, FaChartLine, FaStar, FaQuoteLeft } from 'react-icons/fa';
import apiService from '../services/api';
import CollegeCard from '../components/CollegeCard';
import BlogCard from '../components/BlogCard';
import Loading from '../components/Loading';

const HomePage = () => {
  const [featuredColleges, setFeaturedColleges] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHomeData();
  }, []);

  const fetchHomeData = async () => {
    try {
      const [collegesRes, blogsRes, testimonialsRes] = await Promise.all([
        apiService.getFeaturedColleges(6),
        apiService.getFeaturedBlogs(3),
        apiService.getTestimonials(6)
      ]);

      if (collegesRes.data.success) setFeaturedColleges(collegesRes.data.data);
      if (blogsRes.data.success) setFeaturedBlogs(blogsRes.data.data);
      if (testimonialsRes.data.success) setTestimonials(testimonialsRes.data.data);
    } catch (error) {
      console.error('Error fetching home data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>EduConsult - Expert College Admission Guidance</title>
        <meta name="description" content="Get expert guidance for college admissions. We help students find and get into their dream colleges with personalized consultation." />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-pattern pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-gray-900 mb-6 leading-tight">
                Start Your College Journey With{' '}
                <span className="gradient-text">Expert Guidance</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                Unlock your potential with personalized college admission consultation. 
                We guide you every step of the way to your dream college.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register" className="btn btn-primary text-lg">
                  Register for Consultation
                </Link>
                <Link to="/colleges" className="btn btn-secondary text-lg">
                  Explore Colleges
                </Link>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 mt-12">
                {[
                  { number: '5000+', label: 'Students Guided' },
                  { number: '500+', label: 'College Partners' },
                  { number: '98%', label: 'Success Rate' }
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl font-bold gradient-text mb-1">{stat.number}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative animate-slide-up animation-delay-200">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800"
                  alt="Students studying"
                  className="w-full h-auto"
                />
              </div>
              {/* Floating Cards */}
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="bg-primary-100 p-3 rounded-lg">
                    <FaGraduationCap className="text-primary-600 text-2xl" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Expert Counselors</div>
                    <div className="text-sm text-gray-600">15+ Years Experience</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Why Choose <span className="gradient-text">EduConsult</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We provide comprehensive support throughout your college admission journey
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: FaGraduationCap,
                title: 'Expert Counselors',
                description: '15+ years of experience in college admissions and career guidance'
              },
              {
                icon: FaUsers,
                title: 'Personalized Approach',
                description: 'One-on-one consultation tailored to your goals and interests'
              },
              {
                icon: FaCheckCircle,
                title: 'End-to-End Support',
                description: 'From college selection to admission, we guide you at every step'
              },
              {
                icon: FaChartLine,
                title: 'Proven Success',
                description: '98% success rate with 5000+ students placed in top colleges'
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-xl hover:bg-primary-50 transition-all duration-300 group"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  <feature.icon className="text-white text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding gradient-bg">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Simple Steps to <span className="gradient-text">Get Started</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our streamlined process makes your college admission journey smooth and hassle-free
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Register', description: 'Fill out the consultation form with your details' },
              { step: '02', title: 'Pay Fee', description: 'Make a small consultation payment via UPI' },
              { step: '03', title: 'Consultation', description: 'Get personalized guidance from our experts' },
              { step: '04', title: 'Apply', description: 'Apply to your selected colleges with our support' }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="text-5xl font-bold text-primary-100 mb-4">{item.step}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-primary-300"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Colleges */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-2">
                Top <span className="gradient-text">Colleges</span>
              </h2>
              <p className="text-gray-600">Explore our featured partner institutions</p>
            </div>
            <Link to="/colleges" className="btn btn-secondary hidden md:inline-block">
              View All Colleges
            </Link>
          </div>

          {loading ? (
            <Loading />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredColleges.map((college) => (
                <CollegeCard key={college.id} college={college} />
              ))}
            </div>
          )}

          <div className="text-center mt-12 md:hidden">
            <Link to="/colleges" className="btn btn-secondary">
              View All Colleges
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding gradient-bg">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              What Our <span className="gradient-text">Students Say</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real success stories from students who achieved their dreams with our guidance
            </p>
          </div>

          {loading ? (
            <Loading />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                  <FaQuoteLeft className="text-primary-200 text-3xl mb-4" />
                  <p className="text-gray-700 mb-6 italic">"{testimonial.testimonial}"</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.student_name}</div>
                      <div className="text-sm text-gray-600">{testimonial.college_admitted}</div>
                      <div className="text-xs text-gray-500">{testimonial.course}</div>
                    </div>
                    <div className="flex gap-1">
                      {[...Array(testimonial.rating || 5)].map((_, i) => (
                        <FaStar key={i} className="text-secondary-500" />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Blog Preview */}
      {featuredBlogs.length > 0 && (
        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="flex justify-between items-center mb-12">
              <div>
                <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-2">
                  Latest <span className="gradient-text">Insights</span>
                </h2>
                <p className="text-gray-600">Expert advice and guidance for your college journey</p>
              </div>
              <Link to="/blogs" className="btn btn-secondary hidden md:inline-block">
                View All Articles
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {featuredBlogs.map((blog) => (
                <BlogCard key={blog.id} blog={blog} />
              ))}
            </div>

            <div className="text-center mt-12 md:hidden">
              <Link to="/blogs" className="btn btn-secondary">
                View All Articles
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Book your consultation today and take the first step towards your dream college
          </p>
          <Link to="/register" className="btn bg-white text-primary-600 hover:bg-gray-100 text-lg">
            Get Free Consultation
          </Link>
        </div>
      </section>
    </>
  );
};

export default HomePage;
